import {
  ROOT_URL,
  SEVERITY_LEVELS,
  INCIDENT_STATUS,
} from 'xm-constants/incidentManagement';
import { trimSearch } from '@xmatters/common/utils/searchUtil';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import forOwn from 'lodash/forOwn';

const SERVICE_URI = '/api/xm/1/services';

const BULK_EDIT_INCIDENTS = 'BULK_EDIT_INCIDENTS';
export const BULK_EDIT_INCIDENTS_FAIL = `${BULK_EDIT_INCIDENTS}_FAIL`;
export const BULK_EDIT_INCIDENTS_START = `${BULK_EDIT_INCIDENTS}_START`;
export const BULK_EDIT_INCIDENTS_SUCCESS = `${BULK_EDIT_INCIDENTS}_SUCCESS`;

const BULK_EDIT_BY_FILTERS = 'BULK_EDIT_BY_FILTERS';
export const BULK_EDIT_BY_FILTERS_START = `${BULK_EDIT_BY_FILTERS}_START`;
export const BULK_EDIT_BY_FILTERS_END = `${BULK_EDIT_BY_FILTERS}_END`;

export const SORT_INCIDENTS = 'SORT_INCIDENTS';

export const FETCH_INCIDENTS = 'FETCH_INCIDENTS';
export const FETCH_INCIDENTS_FAIL = `${FETCH_INCIDENTS}_FAIL`;
export const FETCH_INCIDENTS_START = `${FETCH_INCIDENTS}_START`;
export const FETCH_INCIDENTS_SUCCESS = `${FETCH_INCIDENTS}_SUCCESS`;

export const PAGE_INCIDENTS = 'PAGE_INCIDENTS';
export const PAGE_INCIDENTS_FAIL = `${PAGE_INCIDENTS}_FAIL`;
export const PAGE_INCIDENTS_START = `${PAGE_INCIDENTS}_START`;
export const PAGE_INCIDENTS_SUCCESS = `${PAGE_INCIDENTS}_SUCCESS`;

export const SELECT_INCIDENT = 'SELECT_INCIDENT';
export const SELECT_ALL_INCIDENTS = 'SELECT_ALL_INCIDENTS';
export const CLEAR_INCIDENT_SELECTION = 'CLEAR_INCIDENT_SELECTION';

const FETCH_TOTAL_NUM_INCIDENTS = 'FETCH_TOTAL_NUM_INCIDENTS';
export const FETCH_TOTAL_NUM_INCIDENTS_START = `${FETCH_TOTAL_NUM_INCIDENTS}_START`;
export const FETCH_TOTAL_NUM_INCIDENTS_SUCCESS = `${FETCH_TOTAL_NUM_INCIDENTS}_SUCCESS`;

/**
 * Given a filter object, filter out the empty string values.
 * Server doesn't like certain properties to be empty. For example:
 * GET /incidents?search=&ownedByMe=
 * server complains "Invalid search criteria. Searches must include one or more search terms."
 * Weirdly, ownedByMe doesn't have the same issue
 *
 * @param {object} [params={}] - the filters parameter
 * @returns {object} - the filtered parameter.
 */
export function extractNonEmptyParams(params) {
  const nonEmptyParams = {};

  forOwn(params, (value, key) => {
    if (value !== '') {
      nonEmptyParams[key] = value;
    }
  });

  return nonEmptyParams;
}

/**
 * Fetch the correct incidents based on the filter specified in UI. All sorting and filtering are done on the server.
 * The client only does the fetching.
 * @param {object} apiParams - all the params sent to the API.
 *   xm-api doesn't seem to like empty params such as search=&, so will need to weed out the falsy ones (unless it's false!)
 * @property {string} severity: one of MINIMAL, LOW, MEDIUM, HIGH, CRITICAL, undefined. When undefined is used, it'll be filtered out and not included in the URL.
 * @property {boolean} ownedByMe: true/false
 * @property {string} sortBy: STATUS, SEVERITY, SUMMARY, CREATED_AT, OWNER, INCIDENT_IDENTIFIER,
 * @property {string} sortOrder: ASCENDING, DESCENDING
 * @property {string} status: OPEN, IN_PROGRESS, RESOLVED, ALL. xmAPI supports CLOSED as well, but we may not show that option on the client.
 * @property {string} search
 * @property {string} to: date range string to
 * @property {string} from: date range string from
 */
export function fetchIncidents(apiParams) {
  const nonEmptyParams = extractNonEmptyParams(apiParams);

  return {
    type: FETCH_INCIDENTS,
    meta: {
      xmApi: true,
      uri: ROOT_URL,
      method: 'get',
      params: {
        ...nonEmptyParams,
        include: 'services.metrics',
      },
    },
    // Pass through timestamp to prevent handling earlier-but-slower calls that return too late:
    payload: {
      xmRequestTimestamp: +new Date(),
    },
  };
}

export function getNextPage(nextLink) {
  return {
    type: PAGE_INCIDENTS,
    meta: {
      xmApi: true,
      method: 'get',
      uri: nextLink,
      customUriWithParamsGenerator: uriParams => uriParams,
    },
  };
}

export function selectIncident({ id }) {
  return {
    type: SELECT_INCIDENT,
    payload: { id },
  };
}

export function selectAllIncidents() {
  return { type: SELECT_ALL_INCIDENTS };
}

export function clearSelection() {
  return {
    type: CLEAR_INCIDENT_SELECTION,
  };
}

export function bulkEditIncidents({
  incidentIds,
  changedProperty,
  changedValue,
  note,
}) {
  return {
    type: BULK_EDIT_INCIDENTS,
    meta: {
      xmApi: true,
      method: 'post',
      uri: '/webapi/xm/1/batch',
      params: {
        operations: incidentIds.map(id => ({
          method: 'POST',
          uri: '/incidents',
          body: JSON.stringify({
            id,
            [changedProperty]: changedValue,
            note,
          }),
        })),
      },
    },
  };
}

export function bulkEditIncidentsByFilter({
  changedProperty,
  changedValue,
  note,
  // An upper bound of the number of incidents to update
  numRemaining,
  filters = {},
}) {
  return (dispatch, getState) => {
    // First, we have to make sure we even are changing anything, i.e. we are not changing all open incidents to have status open
    // So make sure there's at least one difference
    const changedStat = changedProperty === 'status';
    const changedSev = changedProperty === 'severity';
    const statFilterMatch = isEqual(filters.status, [changedValue]);
    const sevFilterMatch = isEqual(filters.severity, [changedValue]);
    // If (we changed status and the new status matches the filtered status) or (we changed severity and the new severity matches the filtered severity)
    // Basically, if the change we're doing is redundant (i.e. 'Change all Open incidents to have Open status')
    if ((changedStat && statFilterMatch) || (changedSev && sevFilterMatch)) {
      dispatch(clearSelection());
      return Promise.resolve();
    }
    // Show loading
    dispatch({
      type: BULK_EDIT_BY_FILTERS_START,
    });
    // We need to build a list of all the severities/statuses except the one we are changing to
    // So that we don't fetch an incident twice
    // First, get all the possible statues/severities
    // If we are filtering by status/severity, then just get the filter. If not, then get all statuses/severities
    const ALL_SEVERITIES = filters.severity
      ? filters.severity.split(',')
      : Object.values(SEVERITY_LEVELS);

    const ALL_STATUSES = filters.status
      ? filters.status.split(',')
      : Object.values(INCIDENT_STATUS);

    // Then remove the status/severity . we are changing to
    const severity = changedSev
      ? ALL_SEVERITIES.filter(s => s !== changedValue)
      : ALL_SEVERITIES;
    const status = changedStat
      ? ALL_STATUSES.filter(s => s !== changedValue)
      : ALL_STATUSES;

    const getIncidentsWithFilters = () =>
      dispatch(
        fetchIncidents({
          ...filters,
          severity: severity.join(','),
          status: status.join(','),
        }),
      );

    return getIncidentsWithFilters().then(() => {
      // Prevent infinitely running if something goes wrong resolving an incident
      if (
        isEmpty(getState().incidentManagement.incidents.incidentList) ||
        numRemaining <= 0
      ) {
        // Now fetch new incidents to show for the given filters
        // And clear the selection
        return getIncidentsWithFilters()
          .then(() => dispatch(clearSelection()))
          .then(() => dispatch({ type: BULK_EDIT_BY_FILTERS_END }));
      }
      return dispatch(
        bulkEditIncidents({
          incidentIds: getState().incidentManagement.incidents.incidentList.map(
            i => i.id,
          ),
          changedProperty,
          changedValue,
          note,
        }),
      ).then(() => {
        return dispatch(
          bulkEditIncidentsByFilter({
            changedProperty,
            changedValue,
            note,
            numRemaining: numRemaining - 100,
            filters,
          }),
        );
      });
    });
  };
}

export const fetchTotalNumIncidents = () => ({
  type: FETCH_TOTAL_NUM_INCIDENTS,
  meta: {
    xmApi: true,
    method: 'get',
    uri: ROOT_URL,
    params: {
      limit: 1,
    },
  },
});

export const INCIDENT_LIST_PAGE_SEARCH_SERVICES =
  'INCIDENT_LIST_PAGE_SEARCH_SERVICES';
export const INCIDENT_LIST_PAGE_SEARCH_SERVICES_START = `${INCIDENT_LIST_PAGE_SEARCH_SERVICES}_START`;
export const INCIDENT_LIST_PAGE_SEARCH_SERVICES_SUCCESS = `${INCIDENT_LIST_PAGE_SEARCH_SERVICES}_SUCCESS`;
export const INCIDENT_LIST_PAGE_SEARCH_SERVICES_FAIL = `${INCIDENT_LIST_PAGE_SEARCH_SERVICES}_FAIL`;
export const INCIDENT_LIST_PAGE_RESET_SERVICES_SEARCH =
  'INCIDENT_LIST_PAGE_RESET_SERVICES_SEARCH';
export const INCIDENT_LIST_PAGE_ACTIVE_SERVICES_FILTERS =
  'INCIDENT_LIST_PAGE_ACTIVE_SERVICES_FILTERS';

export const setActiveServiceFilters = impactedServices => ({
  type: INCIDENT_LIST_PAGE_ACTIVE_SERVICES_FILTERS,
  payload: impactedServices,
});
export const resetServiceFilterSearch = impactedServices => ({
  type: INCIDENT_LIST_PAGE_RESET_SERVICES_SEARCH,
  payload: impactedServices,
});

export const INCIDENT_LIST_PAGE_SET_ACTIVE_ITEMS =
  'INCIDENT_LIST_PAGE_SET_ACTIVE_ITEMS';
export const setActiveServiceItems = activeServices => ({
  type: INCIDENT_LIST_PAGE_SET_ACTIVE_ITEMS,
  payload: activeServices,
});

export const getServicesBySearch = search => {
  const params = {};

  const trimmedSearchText = trimSearch(search);
  if (trimmedSearchText.length >= 2) {
    params.search = trimmedSearchText;
    params.operand = 'AND';
    params.fields = ['NAME'];
  }

  return {
    type: INCIDENT_LIST_PAGE_SEARCH_SERVICES,
    meta: {
      xmApi: true,
      /**
       *  TODO: probably best to always use `get` - do we actually need getall here?
       *        Perhaps if the user's search returns too many items they should refine the query?
       */
      method: params.search ? 'getall' : 'get',
      uri: SERVICE_URI,
      params,
    },
  };
};

export const INCIDENT_LIST_PAGE_HYDRATE_ACTIVE_SERVICES =
  'INCIDENT_LIST_PAGE_HYDRATE_ACTIVE_SERVICES';
export const INCIDENT_LIST_PAGE_HYDRATE_ACTIVE_SERVICES_START = `${INCIDENT_LIST_PAGE_HYDRATE_ACTIVE_SERVICES}_START`;
export const INCIDENT_LIST_PAGE_HYDRATE_ACTIVE_SERVICES_SUCCESS = `${INCIDENT_LIST_PAGE_HYDRATE_ACTIVE_SERVICES}_SUCCESS`;
export const INCIDENT_LIST_PAGE_HYDRATE_ACTIVE_SERVICES_FAIL = `${INCIDENT_LIST_PAGE_HYDRATE_ACTIVE_SERVICES}_FAIL`;

/**
 * To fetch the list of all the selected services
 * @param {array} activeServiceIds
 * @returns {object} a Redux action
 */
export const hydrateActiveServices = activeServiceIds => {
  const params = {};

  if (activeServiceIds) {
    params.id = activeServiceIds;
  }

  return {
    type: INCIDENT_LIST_PAGE_HYDRATE_ACTIVE_SERVICES,
    meta: {
      xmApi: true,
      method: 'get',
      uri: SERVICE_URI,
      params,
    },
  };
};
